import * as React from "react";
import { useState } from "react";
import {
  Typography,
  TextField,
  Card,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import { CustomButton } from "../components/CustomButton";
import { styles } from "../theme/styles";

/**
 * Modal de "Agregar proveedor" (paso 1).
 * El paso 2 (selección de materiales iniciales) sigue viviendo en el
 * componente padre como <TablaSeleccionMaterial />, este modal solo
 * cubre el formulario de datos generales + clusters + mínimo.
 *
 * Al picarle "Continuar" arma un objeto plano y se lo pasa al padre via
 * onContinue, el padre decide qué hacer (validar id repetido, filtrar
 * materiales por subcategoría, etc — esa lógica no se movió de lugar).
 */
export default function AgregarProveedorModal({
  open,
  clusters,
  subcategories,
  onContinue,
  onCancel,
}) {
  const [nameProvider, setNameProvider] = useState("");
  const [idNumber, setIdNumber] = useState("");
  const [email, setEmail] = useState("");
  const [subcategoria, setSubcategoria] = useState("");
  const [clustersSeleccionados, setClustersSeleccionados] = useState([]);

  const [montoInicial, setMontoInicial] = useState("");

  const [errores, setErrores] = useState({
    nombre: false,
    id: false,
    mail: false,
    mailRegEx: false,
    clusters: false,
    subcategoria: false,
  });

  const toggleCluster = (clusterName) => {
    setClustersSeleccionados((prev) =>
      prev.includes(clusterName)
        ? prev.filter((c) => c !== clusterName)
        : [...prev, clusterName],
    );
    setErrores((prev) => ({ ...prev, clusters: false }));
  };

  const resetForm = () => {
    setNameProvider("");
    setIdNumber("");
    setEmail("");
    setSubcategoria("");
    setClustersSeleccionados([]);
    setMontoInicial("");
    setErrores({
      nombre: false,
      id: false,
      mail: false,
      mailRegEx: false,
      clusters: false,
      subcategoria: false,
    });
  };

  const handleCancelar = () => {
    resetForm();
    onCancel();
  };

  const handleContinuar = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const nuevosErrores = {
      nombre: !nameProvider,
      id: !idNumber,
      mail: !email,
      mailRegEx: email && !emailRegex.test(email),
      clusters: clustersSeleccionados.length === 0,
      subcategoria: !subcategoria,
    };

    if (Object.values(nuevosErrores).some(Boolean)) {
      setErrores(nuevosErrores);
      return;
    }

    onContinue({
      idNumber,
      nameProvider,
      email,
      sucursalAsignada: "",
      clusters: clustersSeleccionados,
      subcategoria,
      montoInicial: montoInicial || null,
    });
  };

  if (!open) return null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 1000,
        backgroundColor: "rgba(58, 58, 58, 0.8)",
        gap: "2rem",
      }}>
      <Card className="div-add-prov">
        <Typography variant="h5" color="primary" style={styles.textin}>
          Agregar proveedor
        </Typography>

        <div className="div-grid-prov">
          <div>
            <p style={{ color: errores.nombre ? "red" : "black" }}>
              Nombre completo:
            </p>
            <TextField
              fullWidth
              size="small"
              value={nameProvider}
              error={errores.nombre}
              helperText={errores.nombre ? "Obligatorio" : ""}
              onChange={(e) => {
                setNameProvider(e.target.value.toUpperCase());
                setErrores((prev) => ({ ...prev, nombre: false }));
              }}
            />
          </div>

          <div>
            <p style={{ color: errores.id ? "red" : "black" }}>ID:</p>
            <TextField
              fullWidth
              size="small"
              type="number"
              value={idNumber}
              error={errores.id}
              helperText={errores.id ? "Obligatorio" : ""}
              onChange={(e) => {
                setIdNumber(e.target.value);
                setErrores((prev) => ({ ...prev, id: false }));
              }}
            />
          </div>

          <div>
            <p
              style={{
                color: errores.mail || errores.mailRegEx ? "red" : "black",
              }}>
              Email:
              {errores.mailRegEx && (
                <span style={{ fontSize: "0.7rem" }}> inválido</span>
              )}
            </p>
            <TextField
              fullWidth
              size="small"
              value={email}
              error={errores.mail || errores.mailRegEx}
              helperText={errores.mail ? "Obligatorio" : ""}
              onChange={(e) => {
                setEmail(e.target.value.toLocaleLowerCase().trim());
                setErrores((prev) => ({ ...prev, mail: false }));
              }}
            />
          </div>

          <div>
            <p style={{ color: errores.subcategoria ? "red" : "black" }}>
              Subcategoría
              {errores.subcategoria && (
                <span style={{ fontSize: "0.7rem" }}> Obligatorio</span>
              )}
            </p>
            <FormControl fullWidth size="small">
              <Select
                error={errores.subcategoria}
                value={subcategoria}
                onChange={(e) => {
                  setSubcategoria(e.target.value);
                  setErrores((prev) => ({ ...prev, subcategoria: false }));
                }}
                MenuProps={{
                  PaperProps: { style: { overflow: "auto", zIndex: "10000" } },
                }}>
                <MenuItem value="">Seleccionar</MenuItem>
                {subcategories &&
                  subcategories.map((sub) => (
                    <MenuItem
                      key={sub.SUBCATEGORIA_ID}
                      value={sub.SUBCATEGORIA_NOMBRE}>
                      {sub.SUBCATEGORIA_NOMBRE}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </div>

          <div>
            <p>
              Mínimo de pedido
              <span
                style={{
                  fontSize: "0.75rem",
                  display: "block",
                  fontWeight: 400,
                  color: "#5B6472",
                }}>
                Opcional — se aplica a todas sus tiendas al crearlo.
              </span>
            </p>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="$0.00 (opcional)"
              value={montoInicial}
              onChange={(e) => setMontoInicial(e.target.value)}
            />
          </div>
        </div>

        <div className="div-clusters">
          <p style={{ color: errores.clusters ? "red" : "black" }}>
            Clústers:
            <span
              style={{
                fontSize: "0.8rem",
                display: "block",
                fontWeight: 400,
              }}>
              Seleccionar cluster(s)
            </span>
            {errores.clusters && (
              <span style={{ fontSize: "0.7rem" }}> Obligatorio</span>
            )}
          </p>
          <div className="cluster-chip-grid">
            {clusters.map((cluster) => {
              const selected = clustersSeleccionados.includes(cluster.name);
              return (
                <div
                  key={cluster.name}
                  className={`cluster-chip${selected ? " selected" : ""}`}
                  onClick={() => toggleCluster(cluster.name)}>
                  <span className="chk-square" />
                  {cluster.name}
                </div>
              );
            })}
          </div>
          {clustersSeleccionados.length > 0 && (
            <p style={{ fontSize: "0.75rem", marginTop: "6px" }}>
              {clustersSeleccionados.length} seleccionados
            </p>
          )}
        </div>

        <div className="div-adm-btn">
          <CustomButton
            className="btn-add-prov btn-guardar"
            onClick={handleContinuar}>
            Continuar
          </CustomButton>
          <CustomButton
            className="btn-add-prov btn-cancelar"
            onClick={handleCancelar}>
            Cancelar
          </CustomButton>
        </div>
      </Card>
    </div>
  );
}
